import { useMemo, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  DatePicker,
  DrawerHeader,
  LoadingIndicator,
  Select,
  TertiaryButton,
} from "@components/v2";
import { TileModal } from "@features/metrics-explorer/components";
import { Chart } from "@features/metrics-explorer/components/metrics/chart";
import { ChartControls } from "@features/metrics-explorer/components/metrics/chart-controls";
import { ChartLegend } from "@features/metrics-explorer/components/metrics/chart-legend";
import { MetricCards } from "@features/metrics-explorer/components/metrics/metric-cards";
import { WarningMessage } from "@features/metrics-explorer/components/warning-message";
import { useMetricsExplorerContext } from "@features/metrics-explorer/hooks";
import { minDateForTimeGranularity } from "@features/sales-v4/utils";
import {
  type DateRangeChoiceV4,
  type FixedDateInterval,
  type ITimeGranularityV2,
  TimeGranularityV2,
} from "@north-beam/nb-common";

import { EmptyState } from "../empty-state";

interface Props {
  dateRange: FixedDateInterval | DateRangeChoiceV4;
  timeGranularity: ITimeGranularityV2;
  onDateRangeChange: (value: FixedDateInterval | DateRangeChoiceV4) => void;
  onTimeGranularityChange: (value: ITimeGranularityV2) => void;
  onClose: () => void;
}

export const MetricsExplorerContent = ({
  dateRange,
  timeGranularity,
  onDateRangeChange,
  onTimeGranularityChange,
  onClose,
}: Props) => {
  const { tiles, metricsData, setTiles } = useMetricsExplorerContext();
  const { minDate } = minDateForTimeGranularity(timeGranularity);

  const [isTileModalOpen, setIsTileModalOpen] = useState(false);

  const isLoading = useMemo(() => {
    return metricsData.some((e) => e.status === "loading");
  }, [metricsData]);

  return (
    <div className="pb-6 flex flex-col h-full">
      <div className="mb-4">
        <DrawerHeader onClose={onClose}>Metrics Explorer</DrawerHeader>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <DatePicker
          className="w-32"
          minDate={minDate}
          value={dateRange}
          onChange={(val) => {
            if (val) {
              onDateRangeChange(val);
            }
          }}
        />
        <Select
          size="md"
          buttonClassName="w-48"
          options={TimeGranularityV2.reactSelectOptions}
          value={timeGranularity}
          onChange={(val) => {
            if (val) {
              onTimeGranularityChange(val);

              if (val === "hourly") {
                onDateRangeChange("last7Days");
                setTiles(
                  tiles.map((e) => {
                    return e.compareDateRange
                      ? { ...e, compareDateRange: { value: "previousPeriod" } }
                      : e;
                  }),
                );
              }
            }
          }}
        />
        <TertiaryButton size="lg" onClick={() => setIsTileModalOpen(true)}>
          <i className="fa-regular fa-plus" /> ADD METRIC
        </TertiaryButton>
      </div>
      <WarningMessage className="p-0 mb-4" />
      <MetricCards
        hideAddButton
        onAddTile={() => null}
        onQuickSelect={() => null}
      />
      <div className="border border-v2-tertiary rounded-lg p-4 bg-v2-primary flex-1 flex flex-col">
        <div className="relative w-full flex-1">
          {isLoading && (
            <div className="absolute left-[50%] top-[50%] translate-x-[-26px] translate-y-[-26px]">
              <LoadingIndicator />
            </div>
          )}
          {tiles.length > 0 ? (
            <div className="h-full flex flex-col">
              <div className="mb-2">
                <ChartControls />
              </div>
              <div className="flex-1">
                <Chart />
              </div>
            </div>
          ) : (
            <EmptyState onClick={() => setIsTileModalOpen(true)} />
          )}
        </div>
        <ChartLegend
          tiles={tiles}
          onChange={(id) => {
            setTiles(
              tiles.map((e) =>
                e.id === id ? { ...e, visible: !e.visible } : e,
              ),
            );
          }}
        />
      </div>
      {isTileModalOpen && (
        <TileModal
          isOpen={isTileModalOpen}
          onClose={() => {
            setIsTileModalOpen(false);
          }}
          onSubmit={(value) => {
            const newTiles = [
              ...tiles,
              {
                id: uuidv4(),
                name: value.name,
                metricId: value.metricId,
                attributionModel: value.attributionModel,
                accountingMode: value.accountingMode,
                attributionWindow: value.attributionWindow,
                compareDateRange: value.compareDateRange,
                filterQuery: value.filterQuery,
                filters: value.filters,
                advancedSearch: value.advancedSearch,
                active: tiles.length === 0,
                visible: true,
                index: tiles.length,
              },
            ];

            setTiles(newTiles);
            setIsTileModalOpen(false);
          }}
        />
      )}
    </div>
  );
};
